/**
 * Fixing the trimester's hiring target.
 *
 * ⚠️ THIS IS WHERE THE TARGET IS SET, and until now that was a fair question to
 * have to ask. The old card wrote to `localStorage` under a line promising it
 * was "shared with the team" — so the commitment existed in one browser, and
 * the Tracking tab showed everyone else "commit a trimester target to see this".
 * It now goes to the server with the identity that committed it.
 *
 * ⚠️ THE TARGET IS NOT THE DEMAND AND NOT THE CAPACITY. Demand is what the
 * business asked for; capacity is what the model says the team can do; the
 * target is what TA undertakes. Committing capacity unchanged is a common and
 * reasonable choice — but it is a CHOICE, so the field is prefilled and
 * editable rather than computed, and what it was decided against is stored with
 * it so "why 34?" is answerable in February.
 */
import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import type { StoredTarget, TargetBasis } from '../../../types/taTracking';
import { Section } from '../tracking/trackingUi';

export function CommitTarget({
  period,
  committed,
  suggestion,
  demand,
  covered,
  basis,
  onCommit,
}: {
  period: string;
  committed: StoredTarget | null;
  /** What the scenario implies — prefilled, never forced. */
  suggestion: number;
  demand: number;
  /** What the team can actually deliver — summed per cluster, never pooled. */
  covered: number;
  basis: TargetBasis;
  onCommit: (target: number, note: string) => Promise<void>;
}) {
  const [value, setValue] = useState<string>(String(suggestion));
  const [lastSuggestion, setLastSuggestion] = useState(suggestion);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  /*
   * Follow the sliders until somebody types.
   *
   * `touched` is what makes that safe: without it, moving a lever after typing
   * a deliberate number silently overwrote it, which is the one edit on this
   * tab that must not be lost to a stray drag.
   */
  const [touched, setTouched] = useState(false);
  if (suggestion !== lastSuggestion) {
    setLastSuggestion(suggestion);
    if (!touched) setValue(String(suggestion));
  }

  /*
   * ⚠️ THE EMPTY FIELD IS THE TRAP. `Number('')` is 0, so clearing the box left
   * the button enabled and committed a target of ZERO — which the server would
   * accept, because zero is a legitimate target, and which Tracking then renders
   * as a trimester TA promised nothing for. The emptiness has to be rejected
   * before the number is looked at.
   */
  const trimmed = value.trim();
  const parsed = Number(trimmed);
  const valid = trimmed !== '' && Number.isFinite(parsed) && parsed >= 0;

  async function submit() {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await onCommit(Math.round(parsed), note.trim());
      setSaved(true);
      setNote('');
      setTouched(false);
      window.setTimeout(() => setSaved(false), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section
      title="The trimester's hiring target"
      aside={committed ? `Committed · ${period}` : `Not committed yet · ${period}`}
    >
      {committed ? (
        <p className="mt-2 text-sm text-gray-700">
          <b className="text-2xl font-semibold tabular-nums text-gray-900">{committed.target}</b>{' '}
          hires — committed by {committed.committedBy.split('@')[0]} on{' '}
          {new Date(committed.committedAt).toLocaleDateString('en-GB')}
          {committed.note && <span className="text-gray-500"> · “{committed.note}”</span>}
        </p>
      ) : (
        <p className="mt-2 text-sm text-gray-500">
          Nothing is committed for this trimester, so the Tracking tab is measuring against demand
          instead. Committing a number here is what turns “are we on track?” into a question about
          a promise rather than about a plan that keeps moving.
        </p>
      )}

      <p className="mt-3 text-xs text-gray-500">
        The scenario above covers <b className="font-semibold text-gray-700">{Math.round(covered)}</b>{' '}
        of <b className="font-semibold text-gray-700">{Math.round(demand)}</b>. Committing what the
        team can cover is a reasonable choice — it is still a choice, which is why this is a field
        and not a calculation.
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        {/*
          Both use the design-system `Input` WITH a label. That matters: with no
          `label` it renders borderless at 16px and a `className` replaces its
          layout classes rather than merging — the deviation CLAUDE.md records
          for the dense Role Prioritisation grid. These two are ordinary labelled
          form fields, which is exactly what the atom is built for.
        */}
        <div className="w-28">
          <Input
            type="number"
            min={0}
            label="Target"
            value={value}
            onChange={(e) => {
              setTouched(true);
              setValue(e.target.value);
            }}
            aria-label="Trimester hiring target"
          />
        </div>
        <div className="min-w-[220px] flex-1">
          <Input
            type="text"
            label="Why this number (optional)"
            value={note}
            maxLength={200}
            onChange={(e) => setNote(e.target.value)}
            aria-label="Why this number"
          />
        </div>
        <Button variant="primary" size="sm" onClick={submit} disabled={saving || !valid}>
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {committed ? 'Re-commit target' : 'Commit target'}
        </Button>
        {saved && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
            <Check className="h-3.5 w-3.5" />
            Saved for everyone
          </span>
        )}
      </div>

      {!valid && (
        <p className="mt-2 text-xs text-amber-800">
          {trimmed === ''
            ? 'Enter a target before committing — an empty box is not zero.'
            : 'That is not a number the target can be.'}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-rose-700">
          That didn’t save: {error}
        </p>
      )}

      <p className="mt-3 text-[11px] text-gray-400">
        Stored with who committed it and what it was decided against —{' '}
        {basis.demandSource === 'baseline' ? 'the frozen demand plan' : 'live demand'},{' '}
        {Math.round(basis.covered ?? 0)} coverable of {Math.round(basis.capacity ?? 0)} capacity over{' '}
        {(basis.horizonMonths ?? 0).toFixed(1)} months. Re-committing keeps the previous one as history rather than replacing it.
      </p>

      {committed && committed.history.length > 0 && (
        <details className="mt-3 text-xs text-gray-500">
          <summary className="cursor-pointer">
            {committed.history.length} earlier{' '}
            {committed.history.length === 1 ? 'commitment' : 'commitments'}
          </summary>
          <ul className="mt-2 space-y-1">
            {committed.history.map((h) => (
              <li key={h.committedAt}>
                <b className="font-semibold tabular-nums text-gray-700">{h.target}</b> ·{' '}
                {h.committedBy.split('@')[0]} ·{' '}
                {new Date(h.committedAt).toLocaleDateString('en-GB')}
                {h.note && <span className="text-gray-400"> · “{h.note}”</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Section>
  );
}
